/**
 * useSharing — the React binding for the Tenant Sharing feature.
 *
 * Strict layering: the SharingPage calls THIS hook; the hook reaches the backend
 * only through the api facade (`api.tenantAccess`) and never touches
 * fetch/ApiClient. It owns:
 *   - the current outbound shares list (ListTenantShares)
 *   - the "Grant access" form + submit (GrantTenantAccess)
 *   - per-share revoke (RevokeTenantAccess) with per-row pending state
 */

import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { ApiError, TenantAccessApi, type TenantShare } from '../../api';
import {
  emptyGrantForm,
  isGrantFormComplete,
  type GrantForm,
  type ShareScope,
} from './types';

export interface UseSharingResult {
  // Shares list
  shares: TenantShare[];
  loading: boolean;
  listError: string | null;
  refreshShares: () => Promise<void>;

  // Grant form
  form: GrantForm;
  setGranteeTenantId: (v: string) => void;
  setScope: (v: ShareScope) => void;
  canGrant: boolean;
  grant: () => Promise<void>;
  granting: boolean;
  grantError: string | null;

  // Revoke (per grantee)
  revoke: (granteeTenantId: string) => Promise<void>;
  /** The grantee id currently being revoked, or null. */
  revokingId: string | null;
  revokeError: string | null;
}

function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

export function useSharing(): UseSharingResult {
  const api = useApi();

  const [shares, setShares] = useState<TenantShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [form, setForm] = useState<GrantForm>(emptyGrantForm());
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const refreshShares = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await api.tenantAccess.listTenantShares();
      setShares(TenantAccessApi.parseShares(res.body));
    } catch (e) {
      setShares([]);
      setListError(errMessage(e, 'Failed to load tenant shares.'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refreshShares();
  }, [refreshShares]);

  const setGranteeTenantId = useCallback((v: string) => {
    setForm((prev) => ({ ...prev, granteeTenantId: v }));
    setGrantError(null);
  }, []);

  const setScope = useCallback((v: ShareScope) => {
    setForm((prev) => ({ ...prev, scope: v }));
    setGrantError(null);
  }, []);

  const canGrant = isGrantFormComplete(form);

  const grant = useCallback(async () => {
    if (!isGrantFormComplete(form)) {
      setGrantError('Enter the grantee tenant id to grant access.');
      return;
    }
    setGranting(true);
    setGrantError(null);
    try {
      await api.tenantAccess.grantTenantAccess({
        granteeTenantId: form.granteeTenantId.trim(),
        scope: form.scope,
      });
      setForm(emptyGrantForm());
      await refreshShares();
    } catch (e) {
      setGrantError(errMessage(e, 'Failed to grant tenant access.'));
    } finally {
      setGranting(false);
    }
  }, [api, form, refreshShares]);

  const revoke = useCallback(
    async (granteeTenantId: string) => {
      setRevokingId(granteeTenantId);
      setRevokeError(null);
      try {
        await api.tenantAccess.revokeTenantAccess({ granteeTenantId });
        await refreshShares();
      } catch (e) {
        setRevokeError(errMessage(e, 'Failed to revoke tenant access.'));
      } finally {
        setRevokingId(null);
      }
    },
    [api, refreshShares],
  );

  return {
    shares,
    loading,
    listError,
    refreshShares,
    form,
    setGranteeTenantId,
    setScope,
    canGrant,
    grant,
    granting,
    grantError,
    revoke,
    revokingId,
    revokeError,
  };
}
